import React from 'react'
import Script from 'dangerous-html/react'
import PropTypes from 'prop-types'
import './calculator.css'

const Calculator = (props) => {
  return (
    <div className={`calculator-container1 ${props.rootClassName}`}>
      <span className="calculator-section-label">Plan your loan</span>
      <h2 className="calculator-section-heading">Loan Calculator</h2>
      <div className="calculator-container2">
        <div className="calculator-container3">
          <React.Fragment>
            <React.Fragment>
              <div className="hmoCalc">
                <div className="hmoHead">
                  <div>
                    <div className="hmoTitle">Loan Calculator</div>
                    <div className="hmoSub">Adjust sliders to see repayment</div>
                  </div>
                </div>
                <div className="hmoGrid">
                  <div className="hmoCard">
                    <div className="hmoLabelRow">
                      <div className="hmoLabel">Loan amount</div>
                      <div className="hmoValue" id="amountText" />
                    </div>
                    <input id="amountSlider" type="range" min="500" max="8000" step="100" defaultValue="3000" />
                    <div className="hmoMinMax"><span>R 500</span><span>R 8 000</span></div>
                  </div>
                  <div className="hmoCard">
                    <div className="hmoLabelRow">
                      <div className="hmoLabel">Months</div>
                      <div className="hmoValue" id="monthsText" />
                    </div>
                    <input id="monthsSlider" type="range" min="1" max="6" step="1" defaultValue="1" />
                    <div className="hmoMinMax"><span>1</span><span>6</span></div>
                  </div>
                </div>
                <div className="hmoResult">
                  <div className="hmoResultTop">
                    <div className="hmoResultTitle">Monthly installment</div>
                    <div className="hmoBig" id="installment">R 0.00</div>
                  </div>
                  <div className="hmoBreakdown">
                    <div className="hmoRow hmoHighlight"><span>Total repay</span><b id="totalRepay">R 0.00</b></div>
                    <div className="hmoRow"><span>Principal</span><b id="principal">R 0.00</b></div>
                    <div className="hmoRow"><span>Interest</span><b id="interest">R 0.00</b></div>
                    <div className="hmoRow"><span>Initiation fee</span><b id="initFee">R 0.00</b></div>
                    <div className="hmoRow"><span>Service fee</span><b id="serviceFee">R 0.00</b></div>
                  </div>
                  <div className="hmoFootnote">Estimates only. Fees/interest depend on your product terms.</div>
                </div>
              </div>

              <style dangerouslySetInnerHTML={{ __html: `
  :root { --bg:#f8f6f1; --blue1:#0a1f3d; --blue2:#0e2c51; --gold:#c9a84c; --white:#ffffff; }
  .hmoCalc,.hmoCalc * { box-sizing:border-box; font-family:'DM Sans',system-ui,sans-serif; }
  .hmoCalc { max-width:100%; margin:0 auto; padding:24px; border-radius:20px; background:linear-gradient(135deg,var(--blue1),var(--blue2)); color:var(--white); }
  .hmoHead { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:14px; }
  .hmoTitle { font-size:20px; font-weight:700; }
  .hmoSub { font-size:12px; opacity:.7; margin-top:4px; }
  .hmoGrid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-top:18px; }
  @media(max-width:720px){ .hmoGrid{ grid-template-columns:1fr; } }
  .hmoCard { background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.10); border-radius:14px; padding:16px; }
  .hmoLabelRow { display:flex; justify-content:space-between; align-items:baseline; gap:10px; margin-bottom:10px; }
  .hmoLabel { font-size:12px; opacity:.72; }
  .hmoValue { font-size:16px; font-weight:800; color:var(--gold); }
  .hmoMinMax { display:flex; justify-content:space-between; font-size:11px; opacity:.65; margin-top:8px; }
  .hmoCalc input[type="range"] { width:100%; accent-color:var(--gold); }
  .hmoResult { margin-top:18px; background:#ffffff; color:var(--blue1); border-radius:16px; padding:20px; }
  .hmoResultTop { display:flex; justify-content:space-between; align-items:flex-end; gap:12px; }
  .hmoResultTitle { font-size:13px; opacity:.65; }
  .hmoBig { font-size:30px; font-weight:900; }
  .hmoBreakdown { margin-top:16px; display:grid; gap:8px; }
  .hmoRow { display:flex; justify-content:space-between; gap:12px; padding:10px 14px; background:var(--bg); border-radius:10px; font-size:14px; }
  .hmoRow b { font-weight:700; }
  .hmoHighlight { border:1px solid rgba(201,168,76,.3); background:rgba(201,168,76,.10); }
  .hmoFootnote { margin-top:12px; font-size:11px; opacity:.65; line-height:1.4; }
` }} />

              <Script>{`
(function(){
  const CONFIG = {
    annualRatePct: 60,
    serviceFeeMonthly: 69,
    initiationFee(amount){ const base=150,pct=0.09,cap=650; return Math.min(cap,base+amount*pct); }
  };
  const moneyZA = n => "R "+Number(n||0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2});
  function get(id){ return document.getElementById(id); }
  function calculate(){
    const aS=get("amountSlider"),mS=get("monthsSlider");
    if(!aS||!mS) return false;
    const amount=parseFloat(aS.value)||0,months=Math.max(1,parseInt(mS.value,10)||1);
    const monthlyRate=(CONFIG.annualRatePct/100)/12;
    const interest=amount*monthlyRate*months;
    const initFee=CONFIG.initiationFee(amount);
    const serviceFees=CONFIG.serviceFeeMonthly*months;
    const totalRepay=amount+interest+initFee+serviceFees;
    const installment=totalRepay/months;
    const set=(id,val)=>{const el=get(id);if(el)el.textContent=val;};
    set("amountText",moneyZA(amount));
    set("monthsText",months===1?"1 month":months+" months");
    set("installment",moneyZA(installment));
    set("totalRepay",moneyZA(totalRepay));
    set("principal",moneyZA(amount));
    set("interest",moneyZA(interest));
    set("initFee",moneyZA(initFee));
    set("serviceFee",moneyZA(serviceFees));
    return true;
  }
  let t=0;const timer=setInterval(()=>{t++;const ok=calculate();if(ok||t>80)clearInterval(timer);},100);
  function bindOnce(){
    const aS=get("amountSlider"),mS=get("monthsSlider");
    if(!aS||!mS)return false;
    if(aS.dataset.bound==="1")return true;
    aS.dataset.bound="1";mS.dataset.bound="1";
    aS.addEventListener("input",calculate);mS.addEventListener("input",calculate);
    return true;
  }
  let tb=0;const timerB=setInterval(()=>{tb++;const ok=bindOnce();if(ok||tb>80)clearInterval(timerB);},100);
})();
`}</Script>
            </React.Fragment>
          </React.Fragment>
        </div>
      </div>
    </div>
  )
}

Calculator.defaultProps = { rootClassName: '' }
Calculator.propTypes = { rootClassName: PropTypes.string }

export default Calculator
